/**
 * Header bar for the Warehouse Layout Editor.
 * Contains title, view mode toggle, undo/redo and action buttons.
 * Lagringsstatus vises i statuslinjen nederst (SelectionStatusBar).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  Save,
  Download,
  Upload,
  Search,
  Settings,
  Undo2,
  Redo2,
  PersonStanding,
  Loader2,
  X,
} from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { InsightMode } from '../hooks/useLayoutReducer';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { ViewMode } from '../types/warehouseLayout';
import { exportLayoutAsJson, importLayoutFromJson } from '../utils/layoutStorage';
import {
  warehouseLayoutService,
  ServerItemSearchResult,
} from '../../../../../services/warehouseLayoutService';

/** Dagvinduene for aktivitet-heatmap */
const INSIGHT_DAY_OPTIONS = [7, 30, 90] as const;

/** Minste antall tegn før varesøket utløses */
const MIN_SEARCH_CHARS = 2;

/** Debounce for varesøk (ms) */
const SEARCH_DEBOUNCE_MS = 400;

/** Norsk tallformat for totalantall i søkeresultatene */
const searchQuantityFormat = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 2 });

/**
 * Varesøk («hvor ligger vare X?») — kompakt felt med resultat-dropdown.
 * Klikk på en vare markerer treff-reolene (eksisterende gul markering i 2D/3D);
 * spesiallokasjoner uten reol velges som enkeltlokasjon når ingen reoler traff.
 */
const ItemSearch: React.FC = () => {
  const { dispatch, warehouseId } = useLayoutEditor();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ServerItemSearchResult[] | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  /** Aktiv markering fra søket — vises som chip ved feltet */
  const [marked, setMarked] = useState<{ itemNo: string; count: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Vern mot utdaterte responser + dobbeltkall (Enter rett før debounce-timeren)
  const requestIdRef = useRef(0);
  const lastRunTermRef = useRef<string | null>(null);

  const runSearch = useCallback(async (term: string) => {
    const requestId = ++requestIdRef.current;
    lastRunTermRef.current = term;
    setSearching(true);
    setSearchError(null);
    try {
      const found = await warehouseLayoutService.findItem(warehouseId, term);
      if (requestId !== requestIdRef.current) return;
      setResults(found);
      setDropdownOpen(true);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      // Nullstill siste kjørte term slik at samme søk kan prøves på nytt etter feil
      lastRunTermRef.current = null;
      setResults(null);
      setSearchError(err instanceof Error ? err.message : String(err));
      setDropdownOpen(true);
    } finally {
      if (requestId === requestIdRef.current) setSearching(false);
    }
  }, [warehouseId]);

  // Debounce: søk automatisk ved >=2 tegn
  useEffect(() => {
    const term = query.trim();
    if (term.length < MIN_SEARCH_CHARS) {
      lastRunTermRef.current = null;
      setResults(null);
      setSearchError(null);
      setDropdownOpen(false);
      return;
    }
    const timer = setTimeout(() => {
      if (lastRunTermRef.current !== term) runSearch(term);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, runSearch]);

  // Klikk utenfor lukker dropdownen
  useEffect(() => {
    if (!dropdownOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [dropdownOpen]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const term = query.trim();
      if (term.length >= MIN_SEARCH_CHARS && lastRunTermRef.current !== term) {
        runSearch(term);
      }
    } else if (e.key === 'Escape') {
      setDropdownOpen(false);
    }
  };

  const handleSelectItem = (item: ServerItemSearchResult) => {
    // Unike reoler blant treffene — får eksisterende gul markering i 2D og 3D
    const rackNumbers = new Set(
      item.locations
        .map(location => location.rackNumber)
        .filter((rack): rack is string => !!rack)
    );
    if (rackNumbers.size > 0) {
      dispatch({ type: 'SET_SELECTION', selectedRacks: rackNumbers });
      setMarked({
        itemNo: item.itemNo,
        count: item.locations.filter(location => !!location.rackNumber).length,
      });
    } else {
      // Kun spesiallokasjoner (rackNumber=null): velg den første som enkeltlokasjon
      const special = item.locations.find(location => !location.rackNumber);
      if (special) {
        dispatch({ type: 'SET_SELECTED_BIN', binCode: special.binCode });
        setMarked({ itemNo: item.itemNo, count: 1 });
      }
    }
    setDropdownOpen(false);
  };

  const clearMarking = () => {
    dispatch({ type: 'SET_SELECTION', selectedRacks: new Set<string>() });
    dispatch({ type: 'SET_SELECTED_BIN', binCode: null });
    setMarked(null);
    setQuery('');
    setResults(null);
    setSearchError(null);
    setDropdownOpen(false);
  };

  return (
    <div ref={containerRef} className="relative flex items-center gap-1.5">
      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results || searchError) setDropdownOpen(true); }}
          placeholder={t('manager.modules.warehouseLayout.itemSearch.placeholder', {}, 'Finn vare...')}
          aria-label={t('manager.modules.warehouseLayout.itemSearch.ariaLabel', {}, 'Finn vare')}
          className="w-44 pl-7 pr-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {searching && (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600 absolute right-2 top-1/2 -translate-y-1/2" />
        )}
      </div>

      {/* Resultat-chip: aktiv markering fra søket */}
      {marked && (
        <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-blue-50 border border-blue-200 text-blue-700 whitespace-nowrap">
          {t(
            'manager.modules.warehouseLayout.itemSearch.markedChip',
            { '0': marked.itemNo, '1': marked.count },
            `${marked.itemNo}: ${marked.count} lokasjoner markert`
          )}
          <button
            onClick={clearMarking}
            className="p-0.5 rounded-full text-blue-500 hover:text-blue-700 hover:bg-blue-100 transition-colors"
            title={t('manager.modules.warehouseLayout.itemSearch.clearMarking', {}, 'Nullstill markering')}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      )}

      {/* Resultat-dropdown */}
      {dropdownOpen && (
        <div className="absolute top-full left-0 mt-1 w-96 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-30">
          {searchError && (
            <p className="px-3 py-2 text-xs text-red-600">
              {t('manager.modules.warehouseLayout.itemSearch.searchFailed', {}, 'Søket feilet')}
              {': '}{searchError}
            </p>
          )}
          {!searchError && results && results.length === 0 && (
            <p className="px-3 py-2 text-sm text-gray-500">
              {t('manager.modules.warehouseLayout.itemSearch.noResults', {}, 'Ingen varer funnet')}
            </p>
          )}
          {!searchError && results && results.map(item => (
            <button
              key={item.itemNo}
              onClick={() => handleSelectItem(item)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-mono font-medium text-gray-900 truncate">{item.itemNo}</p>
                {item.description && (
                  <p className="text-xs text-gray-500 truncate">{item.description}</p>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-medium text-gray-900">
                  {searchQuantityFormat.format(item.totalQuantity)}
                </p>
                <p className="text-xs text-gray-400">
                  {t(
                    'manager.modules.warehouseLayout.itemSearch.locationCount',
                    { '0': item.locations.length },
                    `${item.locations.length} lokasjoner`
                  )}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface EditorHeaderProps {
  onBack?: () => void;
}

const EditorHeader: React.FC<EditorHeaderProps> = ({ onBack }) => {
  const { state, dispatch, save, canUndo, canRedo } = useLayoutEditor();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { layout, editorState } = state;
  if (!layout) return null;

  const handleViewModeChange = (mode: ViewMode) => {
    dispatch({ type: 'SET_VIEW_MODE', mode });
  };

  const handleExport = () => {
    exportLayoutAsJson(layout);
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const imported = await importLayoutFromJson(file);
      imported.warehouseId = layout.warehouseId;
      dispatch({ type: 'IMPORT_LAYOUT', layout: imported });
    } catch (err) {
      dispatch({ type: 'SET_ERROR', error: err instanceof Error ? err.message : t('manager.modules.warehouseLayout.editor.importFailed', {}, 'Import feilet') });
    }
    e.target.value = '';
  };

  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={t('common.buttons.back', {}, 'Tilbake')}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t('manager.modules.warehouseLayout.editor.title', {}, '3D Lagervisning')}</h1>
            <p className="text-sm text-gray-500">
              {layout.name} &bull; {t('manager.modules.warehouseLayout.editor.racksPlaced', { '0': layout.racks.length }, `${layout.racks.length} reoler plassert`)}
              {editorState.selectedRacks.size > 0 && (
                <span className="text-blue-600 ml-2">
                  ({t('manager.modules.warehouseLayout.editor.selectedCount', { '0': editorState.selectedRacks.size }, `${editorState.selectedRacks.size} valgt`)})
                </span>
              )}
            </p>
          </div>
        </div>

        {/* View mode toggle */}
        <div className="flex items-center bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => handleViewModeChange('2d')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              editorState.viewMode === '2d'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('manager.modules.warehouseLayout.editor.view2d', {}, '2D Editor')}
          </button>
          <button
            onClick={() => handleViewModeChange('3d')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              editorState.viewMode === '3d'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('manager.modules.warehouseLayout.editor.view3d', {}, '3D Visning')}
          </button>
          <button
            onClick={() => handleViewModeChange('first-person')}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
              editorState.viewMode === 'first-person'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            title={t('manager.modules.warehouseLayout.editor.firstPersonTooltip', {}, 'Gå rundt i lageret (førsteperson)')}
          >
            <PersonStanding className="w-4 h-4" />
            {t('manager.modules.warehouseLayout.editor.firstPerson', {}, 'Førsteperson')}
          </button>
        </div>

        {/* Varesøk («hvor ligger vare X?») + innsikt-overlay: modus + dagvindu (kun heatmap) + laste-indikator */}
        <div className="flex items-center gap-1.5">
          <ItemSearch />
          <select
            value={state.insightMode}
            onChange={e => dispatch({ type: 'SET_INSIGHT_MODE', mode: e.target.value as InsightMode })}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            title={t('manager.modules.warehouseLayout.insights.selectorTooltip', {}, 'Innsikt-overlay: fargelegg lageret med lagerdata')}
            aria-label={t('manager.modules.warehouseLayout.insights.selectorLabel', {}, 'Innsikt-overlay')}
          >
            <option value="none">{t('manager.modules.warehouseLayout.insights.modeNone', {}, 'Ingen innsikt')}</option>
            <option value="heatmap">{t('manager.modules.warehouseLayout.insights.modeHeatmap', {}, 'Aktivitet')}</option>
            <option value="fill">{t('manager.modules.warehouseLayout.insights.modeFill', {}, 'Fyllingsgrad')}</option>
            <option value="empty">{t('manager.modules.warehouseLayout.insights.modeEmpty', {}, 'Tomme')}</option>
            <option value="dead">{t('manager.modules.warehouseLayout.insights.modeDead', {}, 'Dødlager')}</option>
            <option value="countage">{t('manager.modules.warehouseLayout.insights.modeCountage', {}, 'Telle-alder')}</option>
          </select>
          {state.insightMode === 'heatmap' && (
            <select
              value={state.insightDays}
              onChange={e => dispatch({ type: 'SET_INSIGHT_DAYS', days: Number(e.target.value) })}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              title={t('manager.modules.warehouseLayout.insights.daysTooltip', {}, 'Dagvindu for aktivitet')}
              aria-label={t('manager.modules.warehouseLayout.insights.daysTooltip', {}, 'Dagvindu for aktivitet')}
            >
              {INSIGHT_DAY_OPTIONS.map(days => (
                <option key={days} value={days}>
                  {t('manager.modules.warehouseLayout.insights.daysOption', { '0': days }, `${days} dager`)}
                </option>
              ))}
            </select>
          )}
          {state.insightsLoading && (
            <Loader2
              className="w-4 h-4 animate-spin text-blue-600"
              aria-label={t('manager.modules.warehouseLayout.insights.loading', {}, 'Henter innsiktsdata...')}
            />
          )}
        </div>

        {/* Actions — lagringsstatus vises i statuslinjen nederst */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => dispatch({ type: 'UNDO' })}
            disabled={!canUndo}
            className={`p-2 rounded-lg ${canUndo ? 'hover:bg-gray-100' : 'opacity-30 cursor-not-allowed'}`}
            title={t('manager.modules.warehouseLayout.editor.undoTooltip', {}, 'Angre (Ctrl+Z)')}
          >
            <Undo2 className="w-5 h-5" />
          </button>
          <button
            onClick={() => dispatch({ type: 'REDO' })}
            disabled={!canRedo}
            className={`p-2 rounded-lg ${canRedo ? 'hover:bg-gray-100' : 'opacity-30 cursor-not-allowed'}`}
            title={t('manager.modules.warehouseLayout.editor.redoTooltip', {}, 'Gjenta (Ctrl+Shift+Z)')}
          >
            <Redo2 className="w-5 h-5" />
          </button>

          <div className="w-px h-6 bg-gray-200 mx-1" />

          <button onClick={save} className="p-2 hover:bg-gray-100 rounded-lg" title={t('common.buttons.save', {}, 'Lagre')}>
            <Save className="w-5 h-5" />
          </button>
          <button onClick={handleExport} className="p-2 hover:bg-gray-100 rounded-lg" title={t('manager.modules.warehouseLayout.editor.exportJson', {}, 'Eksporter JSON')}>
            <Download className="w-5 h-5" />
          </button>
          <label className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer" title={t('manager.modules.warehouseLayout.editor.importJson', {}, 'Importer JSON')}>
            <Upload className="w-5 h-5" />
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
            />
          </label>
          <button
            onClick={() => dispatch({ type: 'SET_SIDEBAR_TAB', tab: 'settings' })}
            className={`p-2 rounded-lg ${state.sidebarTab === 'settings' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
            title={t('manager.modules.warehouseLayout.editor.settingsTooltip', {}, 'Innstillinger')}
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditorHeader;
